import { useMemo, memo } from "react";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useDateRange } from "../../contexts/DateRangeContext";
import { useEnergyHistory } from "../../hooks/useHistoricalData";
import { LoadingSkeleton } from "../../components/atoms/LoadingSkeleton/LoadingSkeleton";
import { Card } from "../../components/atoms/Card/Card";
import { FullscreenChart, useIsFullscreen } from "../../components/molecules/FullscreenChart/FullscreenChart";
import { CHART_CONFIG, shouldAnimate } from "../../constants";
import type { EnergyTimeSeries } from "../../../types/energy";
import "./RenewableDistributionChart.css";

// Cache for formatted time strings to avoid repeated expensive toLocaleString calls
const timeFormatCache = new Map<string, string>();

// Helper to format timestamps for chart display with caching
function formatChartTime(ts: string, todayDateStr: string): string {
  const cacheKey = `${ts}-${todayDateStr}`;
  const cached = timeFormatCache.get(cacheKey);
  if (cached) return cached;

  const date = new Date(ts);
  const isToday = date.toLocaleDateString("en-US", { timeZone: "America/Halifax" }) === todayDateStr;

  let result: string;
  if (isToday) {
    result = date.toLocaleTimeString("en-US", {
      timeZone: "America/Halifax",
      hour: "numeric",
      minute: "2-digit",
    });
  } else {
    result = date.toLocaleString("en-US", {
      timeZone: "America/Halifax",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  // Limit cache size to prevent memory leaks
  if (timeFormatCache.size > 5000) {
    timeFormatCache.clear();
  }
  timeFormatCache.set(cacheKey, result);
  return result;
}

// Inner component that consumes fullscreen context - memoized to prevent unnecessary re-renders
interface RenewableChartContentProps {
  chartData: Array<{
    timestamp: string;
    time: string;
    usedOnIsland: number;
    exportedOffIsland: number;
    net: number;
  }>;
  animationsEnabled: boolean;
}

const RenewableChartContent = memo(function RenewableChartContent({ chartData, animationsEnabled }: RenewableChartContentProps) {
  const isFullscreen = useIsFullscreen();

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? "100%" : 400}>
      <AreaChart data={chartData}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={CHART_CONFIG.gridStroke}
        />
        <XAxis
          dataKey="time"
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke }}
        />
        <YAxis
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke }}
          label={{
            value: "Power (MW)",
            angle: -90,
            position: "insideLeft",
            fill: CHART_CONFIG.axisStroke,
          }}
          tickCount={12}
        />
        <Tooltip
          contentStyle={CHART_CONFIG.tooltipStyle}
          formatter={(value: number, name: string) => {
            // Show absolute value for Exported Off Island
            if (name === "Exported Off Island") {
              return [`${Math.round(Math.abs(value))} MW`, name];
            }
            return [`${Math.round(value)} MW`, name];
          }}
        />
        <Legend wrapperStyle={{ color: CHART_CONFIG.axisStroke }} />
        <ReferenceLine y={0} stroke="#666" strokeWidth={1} />
        <Area
          type="monotone"
          dataKey="usedOnIsland"
          fill="#10b981"
          stroke="#10b981"
          fillOpacity={0.6}
          strokeWidth={CHART_CONFIG.strokeWidth}
          name="Used On Island"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Area
          type="monotone"
          dataKey="exportedOffIsland"
          fill="#3b82f6"
          stroke="#3b82f6"
          fillOpacity={0.6}
          strokeWidth={CHART_CONFIG.strokeWidth}
          name="Exported Off Island"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          type="monotone"
          dataKey="net"
          stroke="#a855f7"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Net"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
});

export function RenewableDistributionChart() {
  const { startDate, endDate } = useDateRange();
  const { data: energyResponse, isLoading: energyLoading } =
    useEnergyHistory(startDate, endDate);

  // Defensive: handle both old (raw array) and new ({ data, meta }) API formats
  const energyHistory = Array.isArray(energyResponse)
    ? energyResponse
    : (energyResponse?.data ?? []);

  const chartData = useMemo(() => {
    // Pre-compute today's date string once for all formatting calls
    const todayDateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/Halifax" });

    // Single-pass grouping using Map - O(n) instead of O(n²) with find()
    const renewableByTimestamp = new Map<string, {
      timestamp: string;
      time: string;
      usedOnIsland: number;
      exportedOffIsland: number;
      net: number;
    }>();

    energyHistory.forEach((curr: EnergyTimeSeries) => {
      // Only process renewable power gauges
      if (curr.gaugeType !== "Renewable Power Used On Island" &&
          curr.gaugeType !== "Renewable Power Exported Off Island") {
        return;
      }

      let dataPoint = renewableByTimestamp.get(curr.timestamp);
      if (!dataPoint) {
        dataPoint = {
          timestamp: curr.timestamp,
          time: formatChartTime(curr.timestamp, todayDateStr),
          usedOnIsland: 0,
          exportedOffIsland: 0,
          net: 0,
        };
        renewableByTimestamp.set(curr.timestamp, dataPoint);
      }

      if (curr.gaugeType === "Renewable Power Used On Island") {
        dataPoint.usedOnIsland = curr.value;
      } else if (curr.gaugeType === "Renewable Power Exported Off Island") {
        dataPoint.exportedOffIsland = -curr.value; // Negative for mirror effect
      }
    });

    // Convert Map to sorted array and calculate net in single pass
    return Array.from(renewableByTimestamp.values())
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .map((d) => {
        d.net = d.usedOnIsland + d.exportedOffIsland; // Exported is already negative
        return d;
      });
  }, [energyHistory]);

  // Determine if animations should be enabled based on data size
  const animationsEnabled = shouldAnimate(chartData.length);

  return (
    <Card className="chart-container">
      <FullscreenChart title="Renewable Energy Distribution">
        <h3>Renewable Power Distribution Over Time</h3>
        {energyLoading ? (
          <LoadingSkeleton variant="chart" />
        ) : chartData.length > 0 ? (
          <RenewableChartContent chartData={chartData} animationsEnabled={animationsEnabled} />
        ) : (
          <p className="no-data">
            No renewable power data available for selected time range
          </p>
        )}
      </FullscreenChart>
    </Card>
  );
}
